import React, { memo } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Text from '../Common/NewText'
import Disclaimer from './Disclaimer'
import StockImage from '@/stories/components/Cards/StockMiniCard/StockImage'
import {
  Candle,
  EntityMetadata,
  ExchangeMetaData,
  IMetaInfo,
  TimelineDatum,
} from '@/components/Stocks/Widgets/types'
import { IHeaders } from '@/components/Stocks/Pages/INStockDetailPage/types'
import { calcWatchlistState } from '@/components/watchlists/calcWatchlistState'
import { IND_STOCKS_DOMAIN } from '@/root/constants/common'
import SubHeaderList from '../DetailPage/SubHeaderList'
import { PriceTicker } from '@/components/catalogue/PriceTicker'
import { RUPEE_SYMBOL } from '@/root/constants'
import { getINStockLivePriceUrl } from '@/root/api/apiConstants'

export interface OverviewProps {
  isStocks?: boolean
  short_name?: string
  name?: string
  symbol?: string
  logo?: string
  live_price?: string | number
  percent_change?: string | number
  companyCode?: string
  company_code?: string
  chartData?: Candle[]
  timeline_data?: TimelineDatum[] | null
  fallback?: string
  lineColor?: string
  firebase_path?: string
  showLoginWidget?: boolean
  showDematPersuasion?: boolean
  trading_symbol?: string
  candles_stick_navlink?: string
  graphLoading?: boolean
  loggedIn?: boolean
  isMobile?: boolean | null
  headers?: IHeaders
  metadata?: EntityMetadata
  isDemat?: boolean
  entityClass?: string
  meta_info?: IMetaInfo
  isGraphDisabled?: boolean | false
  last_updated?: string
  removeCurrency?: boolean
  linked_collections?: {
    title?: string
    cta?: string
  }[]
  description?: string
  exchange?: string
  isGlobalIndices?: boolean
  exchange_metadata?: ExchangeMetaData[]
  ind_key?: string
  day_change?: string | number
  isMarketOpen?: boolean
}

const Chart = memo(dynamic(() => import('./INStocksChart')))

const WatchlistCTA = dynamic(
  () => import('@/components/watchlists/WatchlistCTA'),
  {
    ssr: false,
  },
)

/** @deprecated */
const INStocksOverview = (props: OverviewProps) => {
  const {
    isStocks,
    short_name = '',
    name = '',
    symbol = '',
    logo = '',
    live_price = 0,
    percent_change = 0,
    fallback = '',
    firebase_path,
    showLoginWidget,
    showDematPersuasion,
    metadata,
    company_code,
    loggedIn,
    isMobile,
    isDemat,
    entityClass,
    meta_info = {},
    isGraphDisabled = false,
    last_updated,
    removeCurrency = false,
    linked_collections,
    description = '',
    exchange,
    exchange_metadata,
    isGlobalIndices = false,
    ind_key,
    day_change,
    isMarketOpen,
    ...rest
  } = props

  const { is_watchlisted, price_alert_metadata } = metadata ?? {}

  const router = useRouter()

  const watchlist_state = calcWatchlistState(
    is_watchlisted ?? false,
    price_alert_metadata?.is_price_alerted ?? false,
  )
  const list = [
    ...(exchange_metadata?.map(item => ({
      label: `${item.exchange}: ${item.stock_symbol}`,
    })) ?? []),
    ...(symbol && !exchange_metadata?.length
      ? [{ label: `${exchange ? `${exchange}` : 'Quote'}: ${symbol}` }]
      : []),
    ...(linked_collections?.map(item => ({
      label: item.title,
      url: item.cta,
    })) ?? []),
  ]

  return (
    <div>
      <section className="flex items-center justify-between gap-3">
        <div className="flex gap-2 md:items-center md:gap-3">
          {logo && (
            <StockImage
              src={logo}
              alt={`${name} share price logo`}
              fallback={fallback}
            />
          )}
          <div className="flex flex-col gap-y-1">
            <Text appearance="heading2" tag="h1">
              {isStocks ? `${short_name} Share Price` : name}{' '}
            </Text>
            {!!list?.length && <SubHeaderList list={list} />}
          </div>
        </div>

        {company_code && (entityClass === 'STOCK' || entityClass === 'ETF') && (
          <div>
            <WatchlistCTA
              state={watchlist_state}
              watchlist_id={`STOCK_${company_code}`}
              id2={rest.companyCode}
              source="indstock"
              loggedIn={loggedIn}
              kycStates={{
                indKycState: isDemat,
                usKycState: isDemat,
              }}
            />{' '}
          </div>
        )}
      </section>

      {description && (
        <Text appearance="caption" addClassName="text-brand-grey-base my-2.5">
          {description}
        </Text>
      )}

      <section className="my-4 flex flex-wrap items-end justify-between border-b pb-2 md:my-5">
        <PriceTicker
          live_price={Number(live_price)}
          day_change={Number(day_change)}
          day_change_percentage={Number(percent_change)}
          currencySymbol={removeCurrency ? '' : RUPEE_SYMBOL}
          ticker={ind_key}
          apiUrl={getINStockLivePriceUrl}
          isMarketOpen={isMarketOpen}
          last_updated={last_updated}
        />
        {(showLoginWidget || showDematPersuasion) && !isMobile && (
          <Disclaimer
            onClick={() => {
              void router.push(
                `${IND_STOCKS_DOMAIN}/widget/page?page=KYC_FLOW&flowType=INDIAN_STOCKS`,
              )
            }}
          />
        )}
      </section>

      {isGraphDisabled ? null : (
        <div className="h-[280px] md:h-[420px]">
          <Chart
            firebase_path={firebase_path}
            name={name}
            graphEndpointUrl={meta_info.graph_endpoint}
            showTechnicalCta={!meta_info.is_global}
            {...rest}
            isMobile={isMobile}
            is_global={meta_info.is_global}
            removeCurrency={removeCurrency}
            isMarketOpen={isMarketOpen}
          />
        </div>
      )}
    </div>
  )
}

export default INStocksOverview
